import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { brl, effectivePrice, listPrice, hasDiscount } from "@/lib/api";
import { ShoppingCart } from "lucide-react";
import { Button, Thumb } from "@/components/ui-kit";

/**
 * Cartão de produto da vitrine.
 *
 * Passou a mostrar desconto, parcelamento e aviso de estoque baixo. Os três
 * dados já vinham na resposta da API, mas só apareciam na página de detalhe —
 * a informação que decide a compra ficava a um clique de distância.
 */
export default function ProductCard({ product }) {
  const { addItem } = useCart();

  const variants = product.variants || [];
  const inStock = variants.some((v) => v.stock > 0);
  const totalOnline = variants.reduce((s, v) => s + (v.stock || 0), 0);
  const price = effectivePrice(product);
  const onSale = hasDiscount(product);
  const listP = listPrice(product);
  const off = onSale && listP > 0 ? Math.round((1 - price / listP) * 100) : 0;

  return (
    <div className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-brand-500 hover:shadow-md transition-all flex flex-col" data-testid="product-card">
      <Link to={`/produto/${product.slug}`} data-testid="product-card-link" className="relative block">
        <div className="aspect-square overflow-hidden bg-slate-100">
          <Thumb src={product.image} alt={product.name} iconSize={26}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        </div>
        {/* Selos sobre a foto: o desconto é o que faz parar a rolagem, e o
            "últimas unidades" é o que faz decidir agora. */}
        <div className="absolute top-2 left-2 flex flex-col items-start gap-1.5">
          {off > 0 && (
            <span className="text-xs font-bold text-white bg-emerald-600 rounded-full px-2 py-0.5 shadow-sm" data-testid="product-card-off">
              -{off}%
            </span>
          )}
          {inStock && totalOnline <= 5 && (
            <span className="text-[11px] font-semibold text-amber-800 bg-amber-100 rounded-full px-2 py-0.5" data-testid="product-card-low-stock">
              Últimas {totalOnline} un.
            </span>
          )}
        </div>
        {!inStock && (
          <span className="absolute inset-x-0 bottom-0 bg-slate-900/75 text-white text-xs font-semibold text-center py-1.5" data-testid="product-card-out">
            Sem estoque
          </span>
        )}
      </Link>

      <div className="p-4 flex flex-col flex-1">
        <p className="text-eyebrow uppercase text-slate-400">{product.category_slug?.replace(/-/g, " ")}</p>
        {/* `min-h` fixo alinha os preços entre cartões de nome curto e longo. */}
        <Link to={`/produto/${product.slug}`} data-testid="product-card-name"
          className="font-medium text-slate-900 hover:text-brand-600 line-clamp-2 text-sm min-h-[2.5rem] mt-1.5 leading-snug">
          {product.name}
        </Link>

        <div className="mt-auto pt-3 flex items-end justify-between gap-2">
          <div className="leading-tight min-w-0">
            {onSale && (
              <span className="block text-xs text-slate-400 line-through" data-testid="product-card-list-price">{brl(listP)}</span>
            )}
            <span className={`font-heading font-semibold text-lg tabular-nums ${onSale ? "text-emerald-600" : "text-slate-900"}`} data-testid="product-card-price">
              {brl(price)}
            </span>
            {/* Parcelamento: a mesma regra da página de detalhe, agora na vitrine. */}
            <span className="block text-[11px] text-slate-500 mt-0.5">12x de {brl(price / 12)}</span>
          </div>
          <Button
            size="icon"
            onClick={() => inStock && addItem(product, variants.find((v) => v.stock > 0))}
            disabled={!inStock}
            data-testid="product-card-add-btn"
            aria-label={inStock ? `Adicionar ${product.name} ao carrinho` : `${product.name} sem estoque`}
            title={inStock ? "Adicionar ao carrinho" : "Sem estoque"}
            className="shrink-0"
          >
            <ShoppingCart size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
}
